import { ReactElement, ReactNode } from 'react';
import { Stack, TextStyle } from '@shopify/polaris';
import { AdminLanguageOption } from '../hooks/useFormConfig';
import { configContent } from '../localization/configContent';

interface Config {
    client: { id: string; label: string };
    requiredFields: { label: string; id: string; type: string; ref: string; test: boolean }[];
    additionalSettings:
        | {
              type: string;
              component: {
                  type: string;
                  hasAction: boolean;
                  action: (value: string) => void;
                  hasDisableCondition?: () => void;
              };
              label: string;
              getDescription: (value: string, adminLanguage: AdminLanguageOption) => string | ReactNode | ReactElement;
              value: string;
          }[]
        | [];
    additionalRequiredFields:
        | {
              label: string;
              helpText?: string;
              id: string;
              type: string;
              ref: string;
              test?: boolean;
              initialValue: string | boolean;
              disable?: {
                  valueRef: string;
                  value: boolean;
                  label: string;
              };
              attributes?: {
                  maxLength?: number;
                  showCharacterCount: boolean;
              };
              valueControlRegExp?: RegExp;
          }[]
        | [];
}

export const config: Config = {
    client: { id: 'paytrail', label: 'Paytrail' },
    requiredFields: [
        { label: 'Production Merchant Id', id: 'id', type: 'text', ref: 'merchantKey', test: false },
        { label: 'Production Merchant Key', id: 'key', type: 'text', ref: 'merchantSecret', test: false },
        { label: 'Test Merchant Id', id: 'testId', type: 'text', ref: 'testMerchantKey', test: true },
        { label: 'Test Merchant Key', id: 'testKey', type: 'text', ref: 'testMerchantSecret', test: true },
    ],
    additionalSettings: [
        {
            type: 'transactionByTransactionSettlement',
            component: {
                type: 'toggle-button',
                hasAction: true,
                action: () => null,
            },
            label: 'Generate Finnish reference number for transactions',
            getDescription: (_, adminLanguage) => (
                <Stack vertical>
                    <TextStyle>
                        {
                            configContent[adminLanguage][
                                'Enabling this setting will generate Finnish reference number (with checksum) for each transaction. This setting is required only if you are using transaction-by-transaction settlements in Paytrail.'
                            ]
                        }
                    </TextStyle>
                    <TextStyle>
                        {
                            configContent[adminLanguage][
                                'Please note, that the generated reference number will replace Shopify’s payment_id value on the Paytrail’s reference field and it will be passed to Paytrail orderId field instead.'
                            ]
                        }
                    </TextStyle>
                </Stack>
            ),
            value: '',
        },
        {
            type: 'idPrefix',
            component: {
                type: 'text',
                hasAction: true,
                action: () => null,
            },
            label: 'Reference number prefix',
            getDescription: (name: string, adminLanguage) =>
                `${configContent[adminLanguage]['Add custom prefix to the generated reference number']}.`,
            value: '',
        },
        {
            type: 'pendingPayments',
            component: {
                type: 'text',
                hasAction: true,
                action: () => null,
            },
            label: 'Pending Payments',
            getDescription: (name: string, adminLanguage) => (
                <Stack vertical>
                    <TextStyle>
                        {
                            configContent[adminLanguage][
                                'Enabling this setting will enable resolving delayed payments to pending status with Shopify. Please note that an order with a pending payment will reserve a product from the inventory.'
                            ]
                        }
                    </TextStyle>
                </Stack>
            ),
            value: '',
        },
        {
            type: 'vatPercentage',
            component: {
                type: 'text',
                hasAction: true,
                action: () => null,
            },
            label: 'VAT Percentage',
            getDescription: (_, adminLanguage) => (
                <Stack vertical>
                    <TextStyle>
                        {
                            configContent[adminLanguage][
                                'Selecting a value for this setting will enable it to be used for Paytrail payment reports.'
                            ]
                        }
                        .
                    </TextStyle>
                </Stack>
            ),
            value: '',
        },
        {
            type: 'adminLanguage',
            component: {
                type: 'text',
                hasAction: true,
                action: () => null,
            },
            label: 'Admin Language',
            getDescription: (_, adminLanguage) => (
                <Stack vertical>
                    <TextStyle>{configContent[adminLanguage]['Select language.']}.</TextStyle>
                </Stack>
            ),
            value: '',
        },
        // Commented-out until a good UI design is made for "extensions" that are enabled/disabled via external (DB)
        // flag for the controls to be only visible if extension is enabled. The counterparts in the /configure handler
        // have a temporary logic to retain manually entered values even when the UI form won't send them
        //{
        //    type: 'sisIntegration',
        //    component: {
        //        type: 'toggle-button',
        //        hasAction: true,
        //        action: () => null,
        //    },
        //    label: 'Enable Shop-in-shop integration',
        //    getDescription: (name: string) => `Enable integration with the Shop-in-shop service. Do not use!`,
        //    value: '',
        //},
        //{
        //    type: 'sisUrl',
        //    component: {
        //        type: 'text',
        //        hasAction: true,
        //        action: () => null,
        //    },
        //    label: 'Shop-in-shop integration URL',
        //    getDescription: (name: string) => `Specify the Shop-in-shop integration URL. Only usable, if the integration is enabled. Do not use!`,
        //    value: '',
        //},
    ],
    additionalRequiredFields: [
        {
            label: 'Generate Finnish reference number for transactions',
            id: 'transactionByTransactionSettlement',
            type: 'toggle-button',
            ref: 'transactionByTransactionSettlement',
            initialValue: false,
        },
        {
            label: 'Reference Number Prefix',
            id: 'idPrefix',
            type: 'text',
            ref: 'idPrefix',
            initialValue: '',
            disable: {
                valueRef: 'transactionByTransactionSettlement',
                value: false,
                label: '',
            },
            attributes: {
                maxLength: 5,
                showCharacterCount: true,
            },
            valueControlRegExp: /[^0-9]/g,
        },
        {
            label: 'Use pending payment status for delayed payments.',
            id: 'pendingPayments',
            type: 'toggle-button',
            ref: 'pendingPayments',
            initialValue: true,
        },
        {
            label: 'VAT percentage to use for the items field when creating a payment.',
            id: 'vatPercentage',
            type: 'vat-percentage',
            ref: 'vatPercentage',
            initialValue: '',
        },
        {
            label: 'Admin language.',
            id: 'adminLanguage',
            type: 'admin-language',
            ref: 'adminLanguage',
            initialValue: '',
        },
        //{
        //    label: '',
        //    id: 'sisIntegration',
        //    type: 'toggle-button',
        //    ref: 'sisIntegration',
        //    initialValue: false,
        //},
        //{
        //    label: 'Integration URL',
        //    helpText: 'URL for the Shop-in-shop integration service. Do not use!',
        //    id: 'sisUrl',
        //    type: 'url',
        //    ref: 'sisUrl',
        //    initialValue: '',
        //    disable: {
        //        valueRef: 'sisIntegration',
        //        value: false,
        //        label: 'Enabling the Shop-in-shop integration is required for this setting. Do not use!',
        //    },
        //    valueControlRegExp: /[\s]/gi,
        //},
    ],
};

type EnvCredential = Record<string, string | undefined>;

const envCredentials: EnvCredential = {
    TEST_MERCHANT_ID: process.env.REACT_APP_TEST_MERCHANT_ID,
    TEST_MERCHANT_KEY: process.env.REACT_APP_TEST_MERCHANT_KEY,
    TEST_MERCHANT_API_KEY: process.env.REACT_APP_TEST_MERCHANT_API_KEY,
};

const CredentialLabels: Record<string, string> = {
    TEST_MERCHANT_ID: 'Test Merchant Id',
    TEST_MERCHANT_KEY: 'Test Merchant Key',
    TEST_MERCHANT_API_KEY: 'Test Merchant Api Key',
};

export const checkEnvForCredentials = (): EnvCredential[] | undefined => {
    const foundCredentials = Object.keys(envCredentials).filter((key) => envCredentials[key] !== undefined);

    if (foundCredentials.length) {
        return foundCredentials.map((cred) => ({ label: CredentialLabels[cred], value: envCredentials[cred] }));
    } else {
        return;
    }
};
